import type {
  GetApiShipmentsData,
  ShipmentOrigin,
  ShipmentStatus,
} from '@/api';
import type { TableSort } from '@/ui';

import {
  ACTIVE_SHIPMENT_STATUSES,
  ALL_SHIPMENT_STATUSES,
} from '../../../lib/shipmentStatus';
import { formatSort, isSameSort, parseSort } from '../../../lib/tableSort';

/*
 * Состояние списка отгрузок - в search params маршрута, как у справочника
 * товара: в URL только то, что отличается от умолчания.
 */

export const PAGE_SIZE = 20;

/** «Активные» (по умолчанию), «Все статусы» или один статус - как в макете. */
export type StatusFilter = 'active' | 'all' | ShipmentStatus;

/** Поля, по которым бэкенд разрешает сортировать (SearchShipmentsQuery.SortFields). */
export const SORT_FIELDS = [
  'plannedDate',
  'number',
  'createdAt',
  'partnerName',
] as const;

/** Поздние по плановой дате сверху. */
const DEFAULT_SORT: TableSort = { field: 'plannedDate', dir: 'desc' };

/** Порядок по умолчанию строкой - в адресную строку его не пишем. */
export const DEFAULT_SORT_VALUE = formatSort(DEFAULT_SORT);

/**
 * Порядки селекта «Сортировка»: у карточек нет заголовков колонок, поэтому
 * сортировка - выбором из готовых. Другие порядки адресная строка не принимает.
 */
export const SORT_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'plannedDate:desc', label: 'Сначала поздние' },
  { value: 'plannedDate:asc', label: 'Сначала ранние' },
  { value: 'partnerName:asc', label: 'По клиенту' },
  { value: 'number:desc', label: 'Сначала новые' },
];

export interface ShipmentsSearch {
  search?: string;
  status?: StatusFilter;
  /** Происхождение: создана у нас или унаследована от заказа 1С; нет - любое. */
  origin?: ShipmentOrigin;
  /** Только с расхождениями с 1С. */
  discrepancies?: boolean;
  /** Только помеченные задержанными. */
  delayed?: boolean;
  page?: number;
  sort?: string;
}

export interface ResolvedShipmentsSearch {
  search: string;
  status: StatusFilter;
  origin: ShipmentOrigin | 'all';
  discrepancies: boolean;
  delayed: boolean;
  page: number;
  sort: TableSort;
}

const isStatusFilter = (value: unknown): value is StatusFilter =>
  value === 'active' ||
  value === 'all' ||
  (ALL_SHIPMENT_STATUSES as ReadonlyArray<unknown>).includes(value);

/** Происхождение из адресной строки - общее у списка и истории отгрузок. */
export const isShipmentOrigin = (value: unknown): value is ShipmentOrigin =>
  value === 'Culture' || value === 'Onec';

export const validateShipmentsSearch = (
  raw: Record<string, unknown>,
): ShipmentsSearch => {
  const result: ShipmentsSearch = {};

  const search = typeof raw.search === 'string' ? raw.search.trim() : '';
  if (search !== '') result.search = search;

  if (isStatusFilter(raw.status) && raw.status !== 'active')
    result.status = raw.status;

  if (isShipmentOrigin(raw.origin)) result.origin = raw.origin;

  if (raw.discrepancies === true || raw.discrepancies === 'true')
    result.discrepancies = true;

  if (raw.delayed === true || raw.delayed === 'true') result.delayed = true;

  const page = Number(raw.page);
  if (Number.isInteger(page) && page > 1) result.page = page;

  const sort = parseSort(raw.sort, SORT_FIELDS);
  const isOffered = SORT_OPTIONS.some(
    (option) => sort !== undefined && option.value === formatSort(sort),
  );
  if (sort && isOffered && !isSameSort(sort, DEFAULT_SORT))
    result.sort = formatSort(sort);

  return result;
};

export const resolveShipmentsSearch = (
  search: ShipmentsSearch,
): ResolvedShipmentsSearch => ({
  search: search.search ?? '',
  status: search.status ?? 'active',
  origin: search.origin ?? 'all',
  discrepancies: search.discrepancies ?? false,
  delayed: search.delayed ?? false,
  page: search.page ?? 1,
  sort: parseSort(search.sort, SORT_FIELDS) ?? DEFAULT_SORT,
});

/** Статусы для запроса; «все статусы» - фильтра нет вовсе. */
const statusesFor = (
  status: StatusFilter,
): ReadonlyArray<ShipmentStatus> | undefined => {
  if (status === 'all') return undefined;
  if (status === 'active') return ACTIVE_SHIPMENT_STATUSES;
  return [status];
};

export const toShipmentsQuery = (
  resolved: ResolvedShipmentsSearch,
): NonNullable<GetApiShipmentsData['query']> => {
  const query: NonNullable<GetApiShipmentsData['query']> = {
    PageNumber: resolved.page,
    PageSize: PAGE_SIZE,
    Sort: formatSort(resolved.sort),
  };

  const statuses = statusesFor(resolved.status);
  if (statuses) query.Statuses = [...statuses];

  if (resolved.search !== '') query.Search = resolved.search;
  if (resolved.origin !== 'all') query.Origin = resolved.origin;
  if (resolved.discrepancies) query.HasOnecDiscrepancies = true;
  if (resolved.delayed) query.IsDelayed = true;

  return query;
};
